import type {AnyRouter} from "@tanstack/react-router";
import {
    clearWorkspaceContext,
    recordPageLoad,
    setPageContext,
    setRoleContext,
    setWorkspaceContext,
    type PageContext,
    type WorkspaceContext,
} from "~/applicationAnalytics/core/capture";
import {capturePageView} from "~/applicationAnalytics/core/pageview";

export type ResolvedAnalyticsContext = {
    workspace: WorkspaceContext | null;
    role: string | null;
    page: PageContext | null;
};

/** Capture only committed destinations, including browser back/forward navigation. */
export function trackRouterPageViews(
    router: Pick<AnyRouter, "subscribe">,
    resolveContext: (pathname: string) => ResolvedAnalyticsContext,
): () => void {
    let navigationStart: number | null = null;
    let hasResolvedRoute = false;
    const unsubscribeBeforeLoad = router.subscribe("onBeforeLoad", () => {
        navigationStart = performance.now();
    });
    const unsubscribeResolved = router.subscribe("onResolved", ({toLocation}) => {
        try {
            const context = resolveContext(toLocation.pathname);
            capturePageView(() => {
                if (context.workspace === null) {
                    clearWorkspaceContext();
                } else {
                    setWorkspaceContext(context.workspace);
                }
                setRoleContext(context.role);
                setPageContext(context.page);
            });
            // The first route is part of the browser's initial load measurement.
            if (hasResolvedRoute && navigationStart !== null) {
                recordPageLoad({
                    page_load_time_ms: Math.round(performance.now() - navigationStart),
                    load_type: "client_navigation",
                    path: toLocation.pathname,
                });
            }
        } catch {
            // Analytics must never block route resolution.
        } finally {
            navigationStart = null;
            hasResolvedRoute = true;
        }
    });
    return () => {
        unsubscribeBeforeLoad();
        unsubscribeResolved();
    };
}
